import type { GetStaticProps, GetStaticPaths, NextPage } from 'next';
import { LongformPage } from 'pages/Longform/LongformPage';
import type { Longform, LongformSlug } from 'interfaces/content/articles/Post';
import type { HeaderNav } from 'interfaces/navigation/Header';
import { setPianoConfig } from 'services/Piano';
import getLongformBySlug from 'api/Longform/getLongformBySlug';
import getAllLongforms from 'api/Longform/getAllLongforms';
import setLongformPageAdConfig from 'services/Ad/config/setLongformPageAdConfig';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { setHeadData } from 'utils/metaDataUtils/headData';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import type { BypassPaywall } from 'interfaces/BypassPaywall';
import setPageBypassPaywall from 'services/Paywall/setPageBypassPaywall';
import getFooter from 'api/Footer/getFooter';
import mockPageData from 'lib/mockData/page-data';
import type { PreviewData } from 'interfaces/PreviewData';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import { useState, useEffect } from 'react';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import { PianoPaywallPageType } from '@/app_lib/types/piano/Paywall';

interface LongformProps {
  longform: Longform;
}

const LongformNextPage: NextPage<LongformProps> = ({ longform }: LongformProps) => {
  const [adFrequencyViewPort, setAdFrequencyViewPort] = useState<number>(0);

  useEffect(() => {
    const getViewportWidth = () => window.innerWidth;

    const initialViewportWidth = getViewportWidth();

    if (initialViewportWidth < 768) setAdFrequencyViewPort(5);
    else if (initialViewportWidth >= 768) setAdFrequencyViewPort(8);
  }, []);

  return (
    <LongformPage
      longform={longform}
      adFrequency={adFrequencyViewPort}
    />
  );
};

export default LongformNextPage;

interface Props {
  params: {
    slug: string;
  };
}

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { slug } = context.params as { slug: string };
    const longform =
      context.preview && context.previewData
        ? await getLongformBySlug('', context.previewData as PreviewData)
        : await getLongformBySlug(slug);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const longformData = context.preview && context.previewData ? mockPageData : longform;
    const bypassPaywall: BypassPaywall = setPageBypassPaywall(longform);
    const dataLayer = safe(() => setPageGtmConfig(longformData), fallbackData.dataLayer);
    const headData = safe(() => setHeadData(longformData, true), fallbackData.headData);
    const pageAdConfig = safe(() => setLongformPageAdConfig(longform), fallbackData.pageAdConfig);
    const arcSpanPageConfig = safe(() => setarcSpanPageConfig(longformData), fallbackData.arcSpanPageConfig);
    const pianoPageConfig = safe(
      () => setPianoConfig({ pageData: longformData, pageType: PianoPaywallPageType.Article }),
      fallbackData.pianoPageConfig,
    );

    return {
      props: {
        arcSpanPageConfig,
        bypassPaywall,
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        longform,
        pageAdConfig,
        pianoPageConfig,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            slug: 'best-irish-whiskey',
          },
        },
      ],
    };
  }

  const { longforms }: { longforms: LongformSlug[] } = await getAllLongforms();

  const paths: Props[] = longforms.map((longform) => ({
    params: {
      slug: longform.slug,
    },
  }));

  return {
    fallback: 'blocking',
    paths,
  };
};
